<template>
	<div class="header-wrapper" :class="{ 'visible': isVisible }">
		<div class="remote-text" v-if="systemInfo.remote">
			<IconRemote class="icon remote-text-icon"/>
			<p>NEW! Staff can control this TV via phone with Buzztime Remote</p>
		</div>
		<div
				class="header"
				:class="{ 'menu-open': isMenuOpen}"
				@mouseenter="isHovered = true"
				@mouseleave="isHovered = false"
				@click="handleHeaderClick"
				
		>
			<div class="remote-status">
				<div class="status-dot" :class="remoteStatus"></div>
				<IconRemote class="icon"/>
			</div>
			<!--        <div class="header-site-name">{{ systemInfo.handshakeInfo?.siteName }}</div>-->
			<div
					id="header-icon"
					class="header-icon accent-container"
					ref="menuButton"
					:class="{ 'menu-open':isMenuOpen }"
			>
				<div class="header-time">{{ systemInfo.headerDate }}</div>
				<img class="header-icon-img" src="/images/Buzztime_Buddies_Logo_500px-white.png" alt="">
			</div>
		</div>
	</div>
</template>

<script setup>
// Header.vue (Script)
import {ref, onMounted, watch, onUnmounted} from 'vue';
import {systemInfo} from "@/systemInfo.js";
import gsap from 'gsap';
import IconRemote from "@/components/icons/IconRemote.vue";

const props = defineProps({
	isMenuOpen: {
		type: Boolean,
		default: false
	},
	naturallyVisible: {
		type: Boolean,
		default: false
	}
});

const menuButton = ref(null);
const emit = defineEmits(['toggle-menu']);

const isHovered = ref(false);
const isVisible = ref(false);
let hideTimeout = null;

const evaluateVisibility = () => {
	const shouldBeVisible = props.naturallyVisible || props.isMenuOpen || isHovered.value;
	
	if (shouldBeVisible) {
		if (hideTimeout) {
			clearTimeout(hideTimeout);
			hideTimeout = null;
		}
		isVisible.value = true;
	} else {
		if (!hideTimeout) {
			hideTimeout = setTimeout(() => {
				isVisible.value = false;
				hideTimeout = null;
			}, 1000); // Wait 2s before disappearing
		}
	}
};

// Monitor the active inputs of the visibility state machine
watch(
		() => [props.naturallyVisible, props.isMenuOpen, isHovered.value],
		() => evaluateVisibility(),
		{immediate: true}
);

const handleHeaderClick = () => {
	if (isVisible.value) {
		emit('toggle-menu');
	}
};

onUnmounted(() => {
	if (hideTimeout) clearTimeout(hideTimeout);
});

// Single GSAP animation handler for menu state
watch(() => props.isMenuOpen, (isOpen) => {
	const headerIcon = menuButton.value;
	if (!headerIcon) return;
	
	if (isOpen) {
		gsap.to(headerIcon, {
			duration: 0.55,
			ease: 'power2.out'
		});
	} else {
		gsap.to(headerIcon, {
			width: '',
			duration: 0.55,
			ease: 'power2.out'
		});
	}
});

const remoteStatus = ref(null);

watch(() => systemInfo?.remote, (connected) => {
	if (connected === true || connected === 'Connected') remoteStatus.value = 'on';
	else if (connected === false || connected === 'Error') remoteStatus.value = 'off';
	else remoteStatus.value = null;
}, {immediate: true});

defineExpose({
	menuButton
});
</script>

<style scoped>
.header-wrapper {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	pointer-events: none;
	z-index: 1001;
}

.header {
	position: absolute;
	top: var(--spacing-lg);
	right: 0;
	padding-bottom: calc(var(--cqh) * 10);
	padding-left: var(--spacing-xl);
	padding-right: var(--spacing-xl);
	display: flex;
	justify-content: end;
	align-items: center;
	font-size: var(--font-size-reg);
	z-index: 1001;
	opacity: 0.01;
	cursor: pointer;
	pointer-events: auto;
	transition: opacity 0.3s ease;
}

.header-wrapper.visible .header {
	opacity: 1;
}

.header-icon,
.remote-status {
	pointer-events: auto;
}

.header-icon {
	position: relative;
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: calc(var(--spacing-xs) * 0.7);
	background-color: var(--color-popup);
	border-radius: var(--border-radius-full);
	box-shadow: var(--shadow-sm);
	border: var(--border-thin) var(--color-primary-light);
	backdrop-filter: var(--blur-popup);
	-webkit-backdrop-filter: var(--blur-popup);
	cursor: pointer;
	overflow: hidden;
	z-index: 100;
}

.header-icon-img {
	height: var(--font-size-xl);
}

.header-time {
	padding-left: var(--spacing-sm);
	padding-top: calc(var(--cqw) * 0.1);
	letter-spacing: 0.1em;
}

.header-site-name, .header-time {
	font-size: var(--font-size-reg);
	padding-right: var(--spacing-md);
	text-shadow: var(--shadow-sm);
	text-align: end;
}

.remote-text {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: calc(var(--spacing-xs) * 0.7) calc(var(--spacing-md) * 0.7);
	background-color: var(--color-surface-light);
	border-radius: var(--border-radius-full);
	box-shadow: var(--shadow-sm);
	border: var(--border-thin) var(--color-primary-light);
	cursor: pointer;
	overflow: hidden;
	
	background: var(--color-popup);
	backdrop-filter: var(--blur-popup);
	-webkit-backdrop-filter: var(--blur-popup);
	position: absolute;
	top: var(--spacing-lg);
	left: 0;
	margin-left: var(--spacing-xl);
	z-index: 1001;
	
	opacity: 0;
	pointer-events: none;
	transition: opacity 0.3s ease;
}

.header-wrapper.visible .remote-text {
	opacity: 1;
	pointer-events: auto;
}

.remote-status {
	margin-bottom: calc(var(--spacing-md) * -1);
}

.status-dot {
	position: absolute;
	z-index: 1;
	margin-left: 0.3rem;
	width: var(--font-size-sm);
	height: var(--font-size-sm);
	border-radius: 50%;
	background: var(--color-warning);
	filter: drop-shadow(var(--shadow-sm));
}

.status-dot.on {
	background: var(--color-internal-light);
}

.status-dot.off {
	background: var(--color-secondary);
}

.remote-text-icon {
	width: var(--font-size-lg);
}
</style>